// API bindings: catalog (Auto generated)

"use strict";

import { RequestErrorHandler, RequestParams, CommonAuthenticatedErrorHandler, CommonErrorHandler } from "@asanrom/request-browser";
import { getApiUrl, generateURIQuery } from "./utils";
import { CatalogFindResult, AddLocalCatalogResponse, AddLocalCatalogBody, Catalog, ModifyLocalCatalogBody } from "./definitions";

export class ApiCatalog {
    /**
     * Method: GET
     * Path: /catalog
     * Finds catalogs
     * Requires permission: catalog.read
     * @param queryParams Query parameters
     * @returns The request parameters
     */
    public static FindCatalogs(queryParams: FindCatalogsQueryParameters): RequestParams<CatalogFindResult, FindCatalogsErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/catalog` + generateURIQuery(queryParams)),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /catalog
     * Adds a local catalog
     * Requires permission: catalog.write
     * @param body Body parameters
     * @returns The request parameters
     */
    public static AddLocalCatalog(body: AddLocalCatalogBody): RequestParams<AddLocalCatalogResponse, AddLocalCatalogErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/catalog`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_HOMEPAGE", handler.badRequestInvalidHomepage)
                    .add(400, "INVALID_ISSUED", handler.badRequestInvalidIssued)
                    .add(400, "INVALID_IDENTIFIER", handler.badRequestInvalidIdentifier)
                    .add(400, "INVALID_CREATOR", handler.badRequestInvalidCreator)
                    .add(400, "INVALID_CONFORMS_TO", handler.badRequestInvalidConformsTo)
                    .add(400, "INVALID_DESCRIPTION", handler.badRequestInvalidDescription)
                    .add(400, "INVALID_TITLE", handler.badRequestInvalidTitle)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /catalog/{id}
     * Get catalog
     * Requires permission: catalog.read
     * @param id Catalog ID
     * @returns The request parameters
     */
    public static GetCatalog(id: string): RequestParams<Catalog, GetCatalogErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/catalog/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: POST
     * Path: /catalog/{id}
     * Modifies a local catalog
     * Requires permission: catalog.write
     * @param id Catalog ID
     * @param body Body parameters
     * @returns The request parameters
     */
    public static ModifyLocalCatalog(id: string, body: ModifyLocalCatalogBody): RequestParams<void, ModifyLocalCatalogErrorHandler> {
        return {
            method: "POST",
            url: getApiUrl(`/catalog/${encodeURIComponent(id)}`),
            json: body,
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(400, "INVALID_HOMEPAGE", handler.badRequestInvalidHomepage)
                    .add(400, "INVALID_ISSUED", handler.badRequestInvalidIssued)
                    .add(400, "INVALID_IDENTIFIER", handler.badRequestInvalidIdentifier)
                    .add(400, "INVALID_CREATOR", handler.badRequestInvalidCreator)
                    .add(400, "INVALID_CONFORMS_TO", handler.badRequestInvalidConformsTo)
                    .add(400, "INVALID_DESCRIPTION", handler.badRequestInvalidDescription)
                    .add(400, "INVALID_TITLE", handler.badRequestInvalidTitle)
                    .add(400, "*", handler.badRequest)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: DELETE
     * Path: /catalog/{id}
     * Deletes a catalog
     * Requires permission: catalog.write
     * @param id Catalog ID
     * @returns The request parameters
     */
    public static DeleteCatalog(id: string): RequestParams<void, DeleteCatalogErrorHandler> {
        return {
            method: "DELETE",
            url: getApiUrl(`/catalog/${encodeURIComponent(id)}`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "*", handler.notFound)
                    .add(403, "*", handler.forbidden)
                    .add(401, "*", handler.unauthorized)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /catalog/dcat-ap-es.jsonld
     * Serialize the local catalog and its public datasets in DCAT-AP-ES JSON-LD format
     * @returns The request parameters
     */
    public static SerializeCatalogJsonLd(): RequestParams<void, SerializeCatalogJsonLdErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/catalog/dcat-ap-es.jsonld`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NOT_FOUNDDCAT", handler.notFoundNotFounddcat)
                    .add(404, "*", handler.notFound)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /catalog/dcat-ap-es.rdf
     * Serialize the local catalog and its public datasets in DCAT-AP-ES RDF/XML format
     * @returns The request parameters
     */
    public static SerializeCatalogRdf(): RequestParams<void, SerializeCatalogRdfErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/catalog/dcat-ap-es.rdf`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NOT_FOUNDDCAT", handler.notFoundNotFounddcat)
                    .add(404, "*", handler.notFound)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /catalog/dcat-ap-es.ttl
     * Serialize the local catalog and its public datasets in DCAT-AP-ES Turtle format
     * @returns The request parameters
     */
    public static SerializeCatalogTtl(): RequestParams<void, SerializeCatalogTtlErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/catalog/dcat-ap-es.ttl`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(404, "NOT_FOUNDDCAT", handler.notFoundNotFounddcat)
                    .add(404, "*", handler.notFound)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }

    /**
     * Method: GET
     * Path: /catalog/dcat-ap-es
     * Serve the local catalog in the format determined by the Accept header (content negotiation)
     * @returns The request parameters
     */
    public static HandleCatalogContentNegotiation(): RequestParams<void, HandleCatalogContentNegotiationErrorHandler> {
        return {
            method: "GET",
            url: getApiUrl(`/catalog/dcat-ap-es`),
            handleError: (err, handler) => {
                new RequestErrorHandler()
                    .add(406, "NOT_ACCEPTABLErequestedformatnotsupported", handler.status406NotAcceptablerequestedformatnotsupported)
                    .add(406, "*", handler.status406)
                    .add(404, "NOT_FOUNDDCAT", handler.notFoundNotFounddcat)
                    .add(404, "*", handler.notFound)
                    .add(500, "*", "serverError" in handler ? handler.serverError : handler.temporalError)
                    .add("*", "*", "networkError" in handler ? handler.networkError : handler.temporalError)
                    .handle(err);
            },
        };
    }
}

/**
 * Error handler for FindCatalogs
 */
export type FindCatalogsErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Query parameters for FindCatalogs
 */
export interface FindCatalogsQueryParameters {
    /**
     * Local - eg: true,false
     */
    local?: string;

    /**
     * Text to filter by title
     */
    title?: string;

    /**
     * Text to filter by creator
     */
    creator?: string;

    /**
     * Preferred language for texts
     */
    lang?: string;

    /**
     * Keywords separated by commas. Matches catalogs having all of them
     */
    keywords?: string;

    /**
     * Themes separated by commas. Matches catalogs having all of them
     */
    themes?: string;

    /**
     * Keywords separated by commas. Matches catalogs having any of them
     */
    anyKeywords?: string;

    /**
     * Themes separated by commas. Matches catalogs having any of them
     */
    anyThemes?: string;

    /**
     * Issued date range. Separate the 2 dates by a comma
     */
    issued?: string;

    /**
     * Modified date range. Separate the 2 dates by a comma
     */
    modified?: string;

    /**
     * ID of the participant
     */
    participant?: string;

    /**
     * LIst length limit. Default: 25, Max: 256
     */
    limit?: number;

    /**
     * Continuation token
     */
    continuationToken?: string;
}

/**
 * Error handler for AddLocalCatalog
 */
export type AddLocalCatalogErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid title
     */
    badRequestInvalidTitle: () => void;

    /**
     * Invalid description (too big for database)
     */
    badRequestInvalidDescription: () => void;

    /**
     * Invalid conformsTo
     */
    badRequestInvalidConformsTo: () => void;

    /**
     * Invalid creator
     */
    badRequestInvalidCreator: () => void;

    /**
     * Invalid identifier
     */
    badRequestInvalidIdentifier: () => void;

    /**
     * Invalid issued
     */
    badRequestInvalidIssued: () => void;

    /**
     * Invalid homepage
     */
    badRequestInvalidHomepage: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;
};

/**
 * Error handler for GetCatalog
 */
export type GetCatalogErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for ModifyLocalCatalog
 */
export type ModifyLocalCatalogErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 400
     */
    badRequest: () => void;

    /**
     * Invalid title
     */
    badRequestInvalidTitle: () => void;

    /**
     * Invalid description (too big for database)
     */
    badRequestInvalidDescription: () => void;

    /**
     * Invalid conformsTo
     */
    badRequestInvalidConformsTo: () => void;

    /**
     * Invalid creator
     */
    badRequestInvalidCreator: () => void;

    /**
     * Invalid identifier
     */
    badRequestInvalidIdentifier: () => void;

    /**
     * Invalid issued
     */
    badRequestInvalidIssued: () => void;

    /**
     * Invalid homepage
     */
    badRequestInvalidHomepage: () => void;

    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for DeleteCatalog
 */
export type DeleteCatalogErrorHandler = CommonAuthenticatedErrorHandler & {
    /**
     * General handler for status = 403
     */
    forbidden: () => void;

    /**
     * General handler for status = 404
     */
    notFound: () => void;
};

/**
 * Error handler for SerializeCatalogJsonLd
 */
export type SerializeCatalogJsonLdErrorHandler = CommonErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Handler for status = 404 and code = NOT_FOUNDDCAT
     */
    notFoundNotFounddcat: () => void;
};

/**
 * Error handler for SerializeCatalogRdf
 */
export type SerializeCatalogRdfErrorHandler = CommonErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Handler for status = 404 and code = NOT_FOUNDDCAT
     */
    notFoundNotFounddcat: () => void;
};

/**
 * Error handler for SerializeCatalogTtl
 */
export type SerializeCatalogTtlErrorHandler = CommonErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Handler for status = 404 and code = NOT_FOUNDDCAT
     */
    notFoundNotFounddcat: () => void;
};

/**
 * Error handler for HandleCatalogContentNegotiation
 */
export type HandleCatalogContentNegotiationErrorHandler = CommonErrorHandler & {
    /**
     * General handler for status = 404
     */
    notFound: () => void;

    /**
     * Handler for status = 404 and code = NOT_FOUNDDCAT
     */
    notFoundNotFounddcat: () => void;

    /**
     * General handler for status = 406
     */
    status406: () => void;

    /**
     * Handler for status = 406 and code = NOT_ACCEPTABLErequestedformatnotsupported
     */
    status406NotAcceptablerequestedformatnotsupported: () => void;
};

